import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar, ArrowLeft, Clock, Trash2, Eye, EyeOff, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://www.dentalworkerscomp.com";

const scrollToTop = () => window.scrollTo(0, 0);

// SEO helper: sets canonical URL and meta description (mirrors App.js)
const setPageSEO = (path, description) => {
  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = `${SITE_URL}${path}`;
  // Meta description
  if (description) {
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
  }
};

const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/^### (.*?)$/gm, '<h3 class="text-xl font-bold text-slate-900 mt-8 mb-3">$1</h3>')
    .replace(/^## (.*?)$/gm, '<h2 class="text-2xl font-bold text-slate-900 mt-10 mb-4">$1</h2>')
    .replace(/^# (.*?)$/gm, '<h2 class="text-2xl font-bold text-slate-900 mt-6 mb-4">$1</h2>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" class="text-red-600 hover:text-red-500 underline" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^\d+\.\s+(.*?)$/gm, '<li class="ml-4 mb-1">$1</li>')
    .replace(/^- (.*?)$/gm, '<li class="ml-4 mb-1">$1</li>')
    .replace(/(<li.*<\/li>\n?)+/g, '<ul class="list-disc pl-4 mb-4 space-y-1">$&</ul>')
    .replace(/\n\n/g, '</p><p class="text-slate-600 leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>');
  return `<p class="text-slate-600 leading-relaxed mb-4">${html}</p>`;
};

// ==================== BLOG LIST ====================
export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Dental Workers Comp Blog | Trio Insurance";
    setPageSEO("/blog", "Expert insights on workers compensation insurance for California dental practices. Tips, guides, and industry news from Trio Insurance.");
    axios.get(`${API}/blog/posts?published_only=true`)
      .then(r => { setPosts(r.data.posts); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <nav className="bg-white border-b border-slate-200 px-4 py-3 shadow-sm">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/" onClick={scrollToTop}><img src="/trio-logo.png" alt="Trio Insurance" className="h-10" /></Link>
          <Link to="/" onClick={scrollToTop} className="text-red-600 text-sm font-medium">Home</Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Dental Workers Comp <span className="text-red-600">Insights</span></h1>
          <p className="text-slate-500">Expert tips on workers compensation for California dental practices.</p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12"><div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg mb-4">Coming Soon</p>
            <p className="text-slate-400">Expert articles on dental workers comp are on the way.</p>
            <Link to="/" onClick={scrollToTop} className="inline-block mt-6 text-red-600 hover:text-red-500 font-medium">
              &larr; Back to Home
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post, i) => (
              <motion.div key={post.id || i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/blog/${post.slug}`} onClick={scrollToTop}
                  className="block bg-white border border-slate-200 hover:border-red-300 hover:shadow-md rounded-xl p-6 transition-all group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <h2 className="text-xl font-bold group-hover:text-red-600 transition-colors mb-2">{post.title}</h2>
                  <p className="text-slate-500 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(post.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {Math.ceil((post.content?.length || 0) / 1000)} min read</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {posts.length > 0 && (
          <div className="mt-12 text-center">
            <Link to="/" onClick={scrollToTop} className="text-slate-500 hover:text-red-600 font-medium transition-colors">
              &larr; Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== SINGLE BLOG POST ====================
export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => {
        setPost(r.data);
        document.title = `${r.data.title} | Trio Insurance`;
        setPageSEO(`/blog/${slug}`, r.data.excerpt || r.data.title);
        const articleSchema = { "@context": "https://schema.org", "@type": "Article", "headline": r.data.title, "author": { "@type": "Organization", "name": "Trio Insurance Financial Services", "url": "https://www.dentalworkerscomp.com" }, "publisher": { "@type": "Organization", "name": "Trio Insurance Financial Services", "url": "https://www.dentalworkerscomp.com" }, "datePublished": r.data.created_at, "dateModified": r.data.created_at, "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.dentalworkerscomp.com/blog/${r.data.slug}` }, "description": r.data.excerpt || r.data.title, "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["[data-testid='post-title']", "[data-testid='post-content'] p:first-of-type", "[data-testid='post-content'] h2"] } };
        let sc = document.querySelector('#article-schema'); if (!sc) { sc = document.createElement('script'); sc.id = 'article-schema'; sc.type = 'application/ld+json'; document.head.appendChild(sc); }
        sc.textContent = JSON.stringify(articleSchema);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!post) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-900">Post not found.</div>;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <nav className="bg-white border-b border-slate-200 px-4 py-3 shadow-sm">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/" onClick={scrollToTop}><img src="/trio-logo.png" alt="Trio Insurance" className="h-10" /></Link>
          <Link to="/blog" onClick={scrollToTop} className="text-red-600 text-sm font-medium">&larr; All Articles</Link>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
          <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.created_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1"><Clock size={14} /> {Math.ceil((post.content?.length || 0) / 1000)} min read</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-8" data-testid="post-title">{post.title}</h1>
        <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-slate-100" data-testid="post-content"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }} />

        <div className="mt-12 p-6 bg-gradient-to-r from-red-600 to-red-700 rounded-xl text-center">
          <h3 className="text-xl font-bold text-white mb-2">Protect Your Dental Practice Today</h3>
          <p className="text-white/80 mb-4">Get a free workers comp consultation from Trio Insurance.</p>
          
            <a href="https://www.dentalworkerscomp.com/#quote"
            onClick={(e) => { e.preventDefault(); window.location.href = '/#quote'; setTimeout(() => { const el = document.getElementById('quote'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300); }}
            className="inline-block bg-white hover:bg-slate-50 text-red-600 px-8 py-3 rounded-lg font-semibold transition-all cursor-pointer"
            data-testid="blog-cta-btn"
          >
            Get Your Free Quote
          </a>
        </div>

        <div className="mt-8 text-center">
          <Link to="/blog" onClick={scrollToTop} className="text-slate-500 hover:text-red-600 font-medium transition-colors">
            &larr; More Articles
          </Link>
        </div>
      </article>
    </div>
  );
};

// ==================== ADMIN BLOG MANAGER ====================
export const AdminBlogManager = ({ token, api }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const loadPosts = async () => {
    try {
      const res = await axios.get(`${api}/admin/blog/posts`, { headers });
      setPosts(res.data.posts);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadPosts(); // eslint-disable-next-line
  }, []);

  const generatePost = async () => {
    setGenerating(true);
    try {
      await axios.post(`${api}/admin/blog/generate`, {}, { headers });
      await loadPosts();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to generate'); }
    setGenerating(false);
  };

  const togglePublish = async (post) => {
    await axios.patch(`${api}/admin/blog/posts/${post.id}`, { published: !post.published }, { headers });
    await loadPosts();
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    await axios.delete(`${api}/admin/blog/posts/${post.id}`, { headers });
    await loadPosts();
  };

  const saveEdit = async () => {
    await axios.patch(`${api}/admin/blog/posts/${editingPost.id}`, { title: editTitle, content: editContent }, { headers });
    setEditingPost(null);
    await loadPosts();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Blog Posts ({posts.length})</h3>
          <button onClick={generatePost} disabled={generating}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-all ${generating ? 'bg-slate-400' : 'bg-red-600 hover:bg-red-700'}`}
            data-testid="generate-blog-btn">
            <Sparkles size={16} /> {generating ? 'Generating...' : 'Generate New Post'}
          </button>
        </div>

        {editingPost && (
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
            <h4 className="font-bold mb-2">Editing: {editingPost.title}</h4>
            <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full p-2 border rounded mb-2 text-sm" placeholder="Title" />
            <textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="w-full p-2 border rounded h-64 text-sm font-mono" placeholder="Content (Markdown)" />
            <div className="flex gap-2 mt-2">
              <button onClick={saveEdit} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Save</button>
              <button onClick={() => setEditingPost(null)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-medium">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 truncate">{post.title}</h4>
                <p className="text-xs text-slate-500">{new Date(post.created_at).toLocaleDateString()} | {post.published ? 'Published' : 'Draft'}</p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => togglePublish(post)} className={`p-2 rounded-lg ${post.published ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-500'}`} title={post.published ? 'Unpublish' : 'Publish'}>
                  {post.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => { setEditingPost(post); setEditTitle(post.title); setEditContent(post.content); }} className="p-2 rounded-lg bg-blue-100 text-blue-600" title="Edit">
                  <Sparkles size={16} />
                </button>
                <button onClick={() => deletePost(post)} className="p-2 rounded-lg bg-red-100 text-red-600" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
